import { DateOnly } from '@atlassian/jira-platform-utils-date-only/src/index.tsx';
import type { TimelineDuration } from '@atlassian/timeline-table/common/types/timeline';
import type {
	TimelineViewIssue,
	UpdateIssueField,
} from '../../services/get-timeline-view-issues/types.tsx';
import type { DatesUpdate } from './types.tsx';

export const toISOString = (timestamp: number) =>
	DateOnly.fromDateAccordingToUTC(new Date(timestamp)).toISOString();

export const shouldScheduleChildrenIssues = ({
	issue,
	modifiedDates: { startDate, dueDate },
}: {
	issue: TimelineViewIssue;
	modifiedDates: DatesUpdate;
}) => {
	/**
	 * When the issue is a parent
	 * When parent bar is dragged (i.e. both start and due dates are updated), then child bars also dragged together.
	 * When parent bar is resized (i.e. only one of start of due dates are updated), then child bars are unaffected.
	 */

	const { startDate: oldStartDate, dueDate: oldDueDate, hasChildren } = issue;
	const isBothDatesPopulated = startDate !== undefined && dueDate !== undefined;
	const isBothDatesUpdated = startDate !== oldStartDate && dueDate !== oldDueDate;

	return hasChildren && isBothDatesPopulated && isBothDatesUpdated;
};

export const MILLISECONDS_PER_DAY = 86400000;

// An elements end position is not a true reflection of its date, instead it visually extends until end of day
export const DUE_DATE_OFFSET = MILLISECONDS_PER_DAY - 1;

const LEFT = 'LEFT';
const RIGHT = 'RIGHT';
/**
 * Clamped start and due dates to the beginning and end of the timeline.
 *
 * The new dates will take timeline's start and end into consideration and return the correct dates in edge cases such as:
 *  - It will return the existing date if it attempts to move date towards the direction where the date was already intersected the timeline.
 *  - It will return the start OR end of the timeline (depends on the direction) if it attempts to move date outside the timeline.
 */
export const getSafeScheduleDates = ({
	startDate,
	dueDate,
	timelineDuration,
	offset,
}: {
	startDate: number | undefined;
	dueDate: number | undefined;
	timelineDuration: TimelineDuration;
	offset: number;
}): {
	startDate: number | undefined;
	dueDate: number | undefined;
} => {
	const direction = offset < 0 ? LEFT : RIGHT;

	const { startMilliseconds, endMilliseconds } = timelineDuration;
	let newOffset = offset;

	if (direction === LEFT && startDate !== undefined) {
		const maxLeftOffset = -Math.max(0, startDate - startMilliseconds);
		newOffset = Math.max(maxLeftOffset, offset);
	} else if (direction === RIGHT && dueDate !== undefined) {
		const maxRightOffset = Math.max(0, endMilliseconds - dueDate - DUE_DATE_OFFSET);
		newOffset = Math.min(maxRightOffset, offset);
	}

	const newStartDate = startDate !== undefined ? startDate + newOffset : undefined;
	const newDueDate = dueDate !== undefined ? dueDate + newOffset : undefined;

	return { startDate: newStartDate, dueDate: newDueDate };
};

/*
 * This calculates the dates offset
 */

export const getIssuesBulkDatesUpdate = ({
	issue,
	issueChildren,
	dates: { startDate, dueDate },
	timelineDuration,
}: {
	issue: TimelineViewIssue;
	issueChildren: TimelineViewIssue[];
	dates: DatesUpdate;
	timelineDuration: TimelineDuration;
}) => {
	const bulkUpdates: UpdateIssueField[] = [];
	const originalValues: UpdateIssueField[] = [];

	if (issueChildren.length && startDate !== undefined && dueDate !== undefined) {
		let offset = 0;
		const oldStartDate = issue.startDate ?? null;
		const oldDueDate = issue.dueDate ?? null;

		if (oldStartDate != null) {
			offset = startDate - oldStartDate;
		} else if (oldDueDate != null) {
			offset = dueDate - oldDueDate;
		}

		if (offset) {
			issueChildren.forEach((issueChild) => {
				const { startDate: childStartDate, dueDate: childDueDate } = issueChild;

				const { startDate: safeStartDate, dueDate: safeDueDate } = getSafeScheduleDates({
					startDate: childStartDate,
					dueDate: childDueDate,
					timelineDuration,
					offset,
				});

				const newStartDate = safeStartDate != null ? toISOString(safeStartDate) : null;
				const newDueDate = safeDueDate != null ? toISOString(safeDueDate) : null;

				// Exclude issue if the dates have no changes
				if (childStartDate !== safeStartDate || childDueDate !== safeDueDate) {
					originalValues.push({
						id: issueChild.issueId,
						fields: {
							startDate: childStartDate ? toISOString(childStartDate) : null,
							dueDate: childDueDate ? toISOString(childDueDate) : null,
						},
					});
					bulkUpdates.push({
						id: issueChild.issueId,
						fields: { startDate: newStartDate, dueDate: newDueDate },
					});
				}
			});
		}
	}

	return { bulkUpdates, originalValues };
};
